import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  MessageSquarePlus,
  X,
  Bug,
  Lightbulb,
  ThumbsUp,
  AlertTriangle,
  Star,
  Loader2,
  Send,
  CheckCircle2,
  Camera,
  Image as ImageIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FeedbackWidget = () => {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const photoInputRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    rating: 0
  });

  const categories = [
    { value: 'feedback', label: 'General Feedback', icon: ThumbsUp, color: 'text-blue-500' },
    { value: 'bug', label: 'Report a Bug', icon: Bug, color: 'text-red-500' },
    { value: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'text-yellow-500' },
    { value: 'improvement', label: 'Improvement Idea', icon: AlertTriangle, color: 'text-purple-500' }
  ];

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 3 - photos.length;
    const toProcess = files.slice(0, remaining);
    if (files.length > remaining) toast.warning(`Max 3 images. Only ${remaining} more allowed.`);
    toProcess.forEach(file => {
      if (!file.type.startsWith('image/')) return;
      if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => setPhotos(prev => [...prev, { id: Date.now() + Math.random(), file, preview: ev.target.result }]);
      reader.readAsDataURL(file);
    });
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const removePhoto = (id) => setPhotos(prev => prev.filter(p => p.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/feedback/submit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          page: window.location.pathname,
          rating: formData.rating || null
        })
      });

      if (!response.ok) throw new Error('Failed to submit feedback');
      const result = await response.json();

      // Upload photos if any
      if (photos.length > 0 && result.id) {
        const fd = new FormData();
        photos.forEach(p => fd.append('files', p.file));
        const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
        await fetch(`${API_URL}/api/feedback/${result.id}/photos`, {
          method: 'POST',
          headers: authHeaders,
          body: fd,
        });
      }

      setSubmitted(true);
      toast.success('Thank you for your feedback!');
      
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setFormData({ category: '', title: '', description: '', rating: 0 });
        setPhotos([]);
      }, 2000);

    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const StarRating = () => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setFormData({ ...formData, rating: star })}
          className="focus:outline-none"
        >
          <Star
            className={`w-6 h-6 transition-colors ${
              star <= formData.rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300 hover:text-yellow-300'
            }`}
          />
        </button>
      ))}
    </div>
  );

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 z-50 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-lg shadow-purple-500/30 rounded-full h-14 w-14 p-0"
        data-testid="feedback-trigger"
      >
        <MessageSquarePlus className="w-6 h-6" />
      </Button>
    );
  }

  return (
    <div className="fixed bottom-20 right-6 z-50 w-96 max-w-[calc(100vw-3rem)]">
      <Card className="shadow-2xl border-purple-200 dark:border-pink-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">{t('feedback.sendFeedback')}</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
          {user && (
            <p className="text-xs text-gray-500 dark:text-gray-400">Submitting as {user.name}</p>
          )}
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('feedback.thankYou')}</h3>
              <p className="text-gray-600 dark:text-gray-400">{t('feedback.yourFeedbackHelpsUsImprove')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Category Selection */}
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, category: cat.value })}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      formData.category === cat.value
                        ? 'border-purple-500 bg-purple-50 dark:border-pink-500 dark:bg-pink-900/20'
                        : 'border-gray-200 hover:border-purple-200 dark:border-gray-700 dark:hover:border-pink-700'
                    }`}
                  >
                    <cat.icon className={`w-4 h-4 ${cat.color}`} />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{cat.label}</span>
                  </button>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">{t('feedback.title')}</Label>
                <Input
                  id="title"
                  placeholder={t('feedback.briefSummaryOfYourFeedback')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="feedback-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('feedback.description')}</Label>
                <Textarea
                  id="description"
                  placeholder={t('feedback.pleaseProvideDetails')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="feedback-description"
                />
              </div>

              {/* Photo Upload */}
              <div>
                <Label className="mb-1.5 block text-sm">{t('feedback.attachImagesOptionalMax')}</Label>
                <input type="file" ref={photoInputRef} accept="image/*" multiple onChange={handlePhotoSelect} className="hidden" />
                <div className="flex gap-2 flex-wrap">
                  {photos.map((photo) => (
                    <div key={photo.id} className="relative w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-600">
                      <img src={photo.preview} alt="Preview" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removePhoto(photo.id)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5" data-testid="remove-feedback-photo">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {photos.length < 3 && (
                    <button type="button" onClick={() => photoInputRef.current?.click()} className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center text-gray-400 hover:border-pink-400 hover:text-pink-400 transition-colors" data-testid="add-feedback-photo">
                      <Camera className="w-4 h-4" />
                      <span className="text-[10px] mt-0.5">{t('feedback.add')}</span>
                    </button>
                  )}
                </div>
              </div>

              {formData.category === 'feedback' && (
                <div className="space-y-2">
                  <Label>{t('feedback.rateYourExperience')}</Label>
                  <StarRating />
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white"
                disabled={loading}
                data-testid="feedback-submit"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Submit Feedback</>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackWidget;
